import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { ThemeToggle } from '../ui/ThemeToggle';
import { useAuth } from '../../features/auth/useAuth';
import { HardDrive } from 'lucide-react';

export function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <HardDrive className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold text-foreground">DiskRay</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex md:items-center md:space-x-6">
            {user && !isDashboard ? (
              <Link
                to="/dashboard"
                className="text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
              >
                {t('dashboard.overview')}
              </Link>
            ) : !user && (
              <>
                <Link
                  to="/"
                  className="text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
                >
                  {t('navigation.services')}
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
                >
                  {t('navigation.pricing')}
                </Link>
              </>
            )}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            <ThemeToggle />
            <LanguageSwitcher />
            
            {/* Auth Buttons */}
            <div className="hidden md:flex md:items-center md:space-x-2">
              {user ? (
                <>
                  {!isDashboard && (
                    <Link to="/dashboard">
                      <Button variant="ghost" size="sm">
                        {t('dashboard.overview')}
                      </Button>
                    </Link>
                  )}
                  <Button variant="ghost" size="sm" onClick={logout}>
                    {t('auth.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth/login">
                    <Button variant="ghost" size="sm">
                      {t('auth.login')}
                    </Button>
                  </Link>
                  <Link to="/auth/register">
                    <Button variant="primary" size="sm">
                      {t('auth.register')}
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center rounded-md p-2 text-foreground/70 hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary md:hidden"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-200 ease-in-out ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <nav className="space-y-1 px-2 pb-3 pt-2" role="navigation" aria-label="Mobile navigation">
              {user ? (
                <>
                  {!isDashboard && (
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block rounded-md px-3 py-2 text-base font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
                    >
                      {t('dashboard.overview')}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full rounded-md px-3 py-2 text-left text-base font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
                  >
                    {t('auth.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block rounded-md px-3 py-2 text-base font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
                  >
                    {t('navigation.services')}
                  </Link>
                  <Link
                    to="/pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block rounded-md px-3 py-2 text-base font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
                  >
                    {t('navigation.pricing')}
                  </Link>
                  <div className="mt-4 space-y-2 px-3">
                    <Link to="/auth/login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-center">
                        {t('auth.login')}
                      </Button>
                    </Link>
                    <Link to="/auth/register" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="primary" className="w-full justify-center">
                        {t('auth.register')}
                      </Button>
                    </Link>
                  </div>
                </>
              )}
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;
